/**
 * STOP-locked sign-in recovery copy (#2930), shared by web
 * (`components/LoginModal.tsx`) and native (`mobile/app/(auth)/login.tsx` via the
 * `@shared/lib` symlink) so the LEAD wording and the ™ cadence cannot drift
 * between the two surfaces.
 *
 * The backend returns `SMS_OPTED_OUT` when a suppressed phone tries to request a
 * login code (`auth.ts` → `errors.smsOptedOut`). Its message already carries the
 * actionable, env-configured "reply START to <number>" target (see
 * `apiError.ts` → `smsOptOutStartTarget`). A phone-only user's only address is
 * the synthetic `{phone}@sms.oro.fit` with no inbox, so the self-serve door is
 * the shipped add-email recovery surface — reachable only from a live session
 * (`ProfileSettingsPage` → `AddEmailSection` on web; the Account section of
 * `settings/profile.tsx` → `AddEmailSection` on native, both gated on
 * `isSyntheticSmsEmail`). We PREPEND this add-email LEAD to the backend message
 * instead of discarding it: the LEAD points at that recovery surface, and the
 * appended backend copy keeps the precise START target.
 *
 * The LEAD deliberately does NOT itself say "reply START" (the backend message
 * owns that, with the number) and carries NO brand name or ™ — the appended
 * backend message already carries the single ™ (Core Rule 24), so the composed
 * string renders exactly one.
 */
export const SMS_OPTED_OUT_RECOVERY_LEAD =
  "If you're still signed in on another device, open Settings and add an email address so you can always get back in.";

/**
 * Compose the STOP-locked recovery copy: the add-email LEAD followed by the
 * backend's own SMS_OPTED_OUT message (which carries the precise "reply START to
 * <number>" target). Falls back to the bare LEAD if the backend supplied no
 * message. Used by both the web request/resend SMS_OPTED_OUT branches and the
 * native login onSettled override.
 *
 * Accepts a minimal `{ message }` shape rather than a concrete `ApiError` type so
 * it stays transport-agnostic across the web and native error classes.
 */
export function smsOptedOutRecoveryCopy(apiError: { message?: string | null }): string {
  const backendMessage = apiError.message?.trim();
  return backendMessage
    ? `${SMS_OPTED_OUT_RECOVERY_LEAD} ${backendMessage}`
    : SMS_OPTED_OUT_RECOVERY_LEAD;
}
